import { Box, Avatar, Typography, Chip, Tooltip, Stack } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import GroupIcon from '@mui/icons-material/Group';
import AdminTable from '../shared/AdminTable';
import AdminStatusBadge from '../shared/AdminStatusBadge';
import { AdminUser, AdminTableColumn, AdminPagination } from '../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface UsersTableProps {
  users: AdminUser[];
  isLoading: boolean;
  error: string | null;
  pagination: AdminPagination;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onViewUser: (user: AdminUser) => void;
  onBanUser: (user: AdminUser) => void;
  onUnbanUser: (user: AdminUser) => void;
  onDeleteUser: (user: AdminUser) => void;
}

/**
 * UsersTable - Main users table with actions
 *
 * Features:
 * - User avatar and name display
 * - Email with mailto link
 * - Account type badges (Business, Agent, Has Agent)
 * - Subscription tier display
 * - Active homes count
 * - Created date with relative time
 * - Status badge
 * - Row actions: View, Ban/Unban, Delete
 * - Pagination
 */
export default function UsersTable({
  users,
  isLoading,
  error,
  pagination,
  onPageChange,
  onLimitChange,
  onViewUser,
  onBanUser,
  onUnbanUser,
  onDeleteUser,
}: UsersTableProps) {
  const getTierColor = (tier: string): 'default' | 'primary' | 'secondary' | 'success' => {
    switch (tier) {
      case 'free':
        return 'default';
      case 'pro':
        return 'primary';
      case 'agent':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTierLabel = (tier: string): string => {
    switch (tier) {
      case 'free':
        return 'Free';
      case 'pro':
        return 'Pro';
      case 'agent':
        return 'Agent';
      default:
        return tier;
    }
  };

  const columns: AdminTableColumn<AdminUser>[] = [
    {
      key: 'name',
      label: 'User',
      sortable: true,
      width: 250,
      render: (user) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={user.picture} alt={user.name} sx={{ width: 40, height: 40 }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {user.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="a"
              href={`mailto:${user.email}`}
              sx={{
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {user.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'is_business',
      label: 'Account Type',
      width: 200,
      render: (user) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {user.is_business && (
            <Tooltip title="Business Owner">
              <Chip
                icon={<BusinessIcon />}
                label="Business"
                size="small"
                color="secondary"
                variant="outlined"
              />
            </Tooltip>
          )}
          {user.is_agent && !user.is_business && (
            <Tooltip title="Agent">
              <Chip
                icon={<BadgeIcon />}
                label="Agent"
                size="small"
                color="info"
                variant="outlined"
              />
            </Tooltip>
          )}
          {user.has_agent && (
            <Tooltip title="Has Agent">
              <Chip
                icon={<GroupIcon />}
                label="Has Agent"
                size="small"
                color="default"
                variant="outlined"
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      key: 'subscription_tier',
      label: 'Subscription',
      sortable: true,
      width: 120,
      render: (user) => (
        <Chip
          label={getTierLabel(user.subscription_tier)}
          size="small"
          color={getTierColor(user.subscription_tier)}
        />
      ),
    },
    {
      key: 'active_homes_count',
      label: 'Homes',
      sortable: true,
      width: 80,
      render: (user) => (
        <Typography variant="body2" align="center">
          {user.active_homes_count}
        </Typography>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 150,
      render: (user) => (
        <Tooltip title={new Date(user.created_at).toLocaleString()}>
          <Typography variant="body2" color="text.secondary">
            {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 100,
      render: (user) => <AdminStatusBadge status={user.status} />,
    },
  ];

  return (
    <AdminTable
      columns={columns}
      data={users}
      isLoading={isLoading}
      error={error}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        onPageChange,
        onLimitChange,
      }}
      actions={[
        {
          label: 'View Details',
          icon: <VisibilityIcon />,
          onClick: onViewUser,
          color: 'primary',
        },
        {
          label: 'Ban User',
          icon: <BlockIcon />,
          onClick: onBanUser,
          color: 'error',
          disabled: (user) => user.status === 'banned',
        },
        {
          label: 'Unban User',
          icon: <CheckCircleIcon />,
          onClick: onUnbanUser,
          color: 'success',
          disabled: (user) => user.status === 'active',
        },
        {
          label: 'Delete User',
          icon: <DeleteIcon />,
          onClick: onDeleteUser,
          color: 'error',
        },
      ]}
      emptyState={
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No users found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search criteria
          </Typography>
        </Box>
      }
    />
  );
}
